import { sportsnewOddStateIdOf } from './sportsnewOddStateId.js';
import { selectedPatchFreshness } from './sportsnewSelectedRecoveryPolicy.js';

export const SPORTSNEW_SELECTED_RECOVERY_LIMIT = 50;

export const normalizeSelectedRecoveryIds = (values = []) => [...new Set(
  (Array.isArray(values) ? values : [])
    .map(sportsnewOddStateIdOf)
    .filter((value) => value != null),
)]
  .sort((left, right) => left - right)
  .slice(0, SPORTSNEW_SELECTED_RECOVERY_LIMIT);

const recoveryPayload = (source = {}) => {
  if (Array.isArray(source?.odds)) return source;
  if (Array.isArray(source?.data?.odds)) return source.data;
  if (Array.isArray(source?.data?.data?.odds)) return source.data.data;
  return source?.data && typeof source.data === 'object' ? source.data : source;
};

const finiteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const selectedLifecyclePatch = (odd = {}, priceDecimal) => {
  const state = String(odd.state ?? odd.displayState ?? odd.display_state ?? '')
    .trim()
    .toUpperCase();
  const unavailable = odd.unavailable === true || state === 'UNAVAILABLE';
  const suspended = odd.suspended === true
    || odd.is_suspended === true
    || odd.marketSuspended === true
    || odd.market_suspended === true
    || state === 'SUSPENDED';
  const explicitlyClosed = unavailable
    || suspended
    || odd.bettable === false
    || odd.isBettable === false
    || odd.is_bettable === false
    || odd.oddBettable === false
    || odd.odd_bettable === false
    || odd.marketBettable === false
    || odd.market_bettable === false
    || odd.locked === true
    || state === 'LOCKED';
  if (explicitlyClosed) {
    return {
      bettable: false,
      locked: !unavailable,
      is_suspended: suspended,
      ...(unavailable ? { unavailable: true } : {}),
    };
  }

  const explicitlyOpen = odd.bettable === true
    || odd.isBettable === true
    || odd.is_bettable === true
    || (
      (odd.oddBettable === true || odd.odd_bettable === true)
      && (odd.marketBettable === true || odd.market_bettable === true)
      && !suspended
    )
    || ['OPEN', 'AVAILABLE'].includes(state);
  if (explicitlyOpen && priceDecimal != null) {
    return { bettable: true, locked: false, is_suspended: false };
  }
  // A price-only row has no lifecycle authority and may never reopen an
  // already locked/unavailable cart leg.
  return {};
};

export const normalizeSelectedRecoveryResult = (source, requestedValues = []) => {
  const requestedIds = normalizeSelectedRecoveryIds(requestedValues);
  const requested = new Set(requestedIds);
  const payload = recoveryPayload(source);
  const odds = Array.isArray(payload?.odds) ? payload.odds : [];
  const found = new Set();
  const updates = [];

  odds.forEach((odd) => {
    const oddStateId = sportsnewOddStateIdOf(odd);
    if (oddStateId == null || !requested.has(oddStateId) || found.has(oddStateId)) return;
    found.add(oddStateId);
    const priceDecimal = finiteNumberOrNull(
      odd.priceDecimal ?? odd.price_decimal ?? odd.odds ?? odd.price,
    );
    const patch = selectedLifecyclePatch(odd, priceDecimal);
    updates.push({
      oddStateId,
      priceDecimal,
      ...selectedPatchFreshness({ ...odd, patch }, null, null),
      patch,
    });
  });

  const explicitMissing = normalizeSelectedRecoveryIds(
    payload?.missingOddStateIds ?? payload?.missing_odd_state_ids ?? [],
  );
  const missing = new Set([
    ...requestedIds.filter((oddStateId) => !found.has(oddStateId)),
    ...explicitMissing.filter((oddStateId) => requested.has(oddStateId)),
  ]);

  missing.forEach((oddStateId) => {
    updates.push({
      oddStateId,
      priceDecimal: null,
      sourceReceivedAt: null,
      updatedAt: null,
      patch: { unavailable: true, bettable: false },
    });
  });

  return {
    requestedIds,
    updates,
    missingOddStateIds: [...missing].sort((left, right) => left - right),
  };
};
